import { CheckCircleOutline } from "@mui/icons-material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import { Accordion, AccordionDetails, AccordionSummary, Box, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import {
  trackEarlyPaymentGSTCertUploaded,
  trackEarlyPaymentSubmitted,
  trackEarlyPaymentViewed,
} from "src/Utils/Mixpanel/VendorPortalEvents";
import { uiLoggerNamesVendorPortal } from "src/Utils/Recon/UiLogger/Constants";
import { useVendorPortal } from "./VendorPortalLayout";

export enum RequestType {
  REQUEST_CALLBACK = "Request a Callback",
  GET_ONBOARDED = "Get Onboarded",
}

const EarlyPaymentVendorPortal: React.FC = () => {
  const { token, bannerData, setBannerData, uiLogsVendorPortal } = useVendorPortal();
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    emailId: "",
    contactNumber: "",
    creditPeriod: "",
    gstCertificate: null as File | null,
  });

  // Track on mount
  React.useEffect(() => {
    trackEarlyPaymentViewed({ enrolment_state: bannerData?.hasFilledForm ? "enrolled" : "not_enrolled" });
  }, []);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, gstCertificate: e.target.files[0] });
      trackEarlyPaymentGSTCertUploaded({ file_selected: true });
    }
  };

  const submitFormData = async (requestType) => {
    if (!formData.name.trim()) {
      toast.error(<CustomToast message="Please enter your name." />);
      return;
    }
    if (!formData.designation.trim()) {
      toast.error(<CustomToast message="Please enter your designation." />);
      return;
    }
    if (!formData.emailId.trim()) {
      toast.error(<CustomToast message="Please enter your email ID." />);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId.trim())) {
      toast.error(<CustomToast message="Please enter a valid email ID." />);
      return;
    }
    if (!formData.contactNumber.trim()) {
      toast.error(<CustomToast message="Please enter your contact number." />);
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.contactNumber.trim())) {
      toast.error(<CustomToast message="Please enter a valid 10-digit mobile number." />);
      return;
    }
    if (!formData.creditPeriod.trim()) {
      toast.error(<CustomToast message="Please enter the credit period." />);
      return;
    }
    const bodyFormData = new FormData();

    bodyFormData.append("file", formData.gstCertificate as Blob);
    bodyFormData.append("name", formData.name);
    bodyFormData.append("email", formData.emailId);
    bodyFormData.append("designation", formData.designation);
    bodyFormData.append("contact", formData.contactNumber);
    bodyFormData.append("creditPeriod", String(parseInt(formData.creditPeriod)));
    bodyFormData.append("requestType", requestType); // Use the passed request type

    await useFetch(API_ENDPOINTS.VENDOR_PORTAL_EARLY_PAYMENT_RESPONSE.url, "POST", {
      failureMessage: API_ENDPOINTS.VENDOR_PORTAL_EARLY_PAYMENT_RESPONSE.failureMessage,
      // showSuccessToast: true,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
      data: bodyFormData,
      thenCallBack: (_response) => {
        setBannerData({ ...bannerData, hasFilledForm: true });
        toast.success(<CustomToast message="Details submitted successfully!" />);
      },
      catchCallBack: (error) => {
        console.error("Error submitting form", error);
      },
    });
  };

  const faqItems = [
    {
      question: "What is the Early Payment Program?",
      answer:
        "It is a cash-discount program allowing vendors to receive up to 90% of approved invoice value early in exchange for a small discount.",
    },
    {
      question: "Who is eligible?",
      answer: "All Scootsy Logistics vendors supplying for at least 3 months.",
    },
    {
      question: "How does it work?",
      answer: "Once you opt in, all approved invoices are automatically processed for early payment.",
    },
    {
      question: "Is this bank finance?",
      answer:
        "No. This is not a bank or invoice-discounting product. It is an off-balance-sheet cash discount program.",
    },
    {
      question: "When will I receive early payment?",
      answer: "Typically within 3 working days of invoice approval.",
    },
    {
      question: "What % of the invoice will be paid early?",
      answer: "Up to 90% of the approved invoice value.",
    },
    {
      question: "How is the cash discount calculated?",
      answer: "Based on the number of days advanced and the applicable discount rate.",
    },
    {
      question: "Are all invoices included automatically?",
      answer: "Yes. After opting in, every approved invoice is included unless you opt out.",
    },
    {
      question: "Will this change my regular payment terms?",
      answer: "No. Base commercial terms remain the same.",
    },
    {
      question: "Can I opt out later?",
      answer: "Yes, you may opt out at any time after completing 3 months.",
    },
    {
      question: "Does this affect my credit score or bank limits?",
      answer: "No impact. This is not a financial borrowing.",
    },
    {
      question: "Will I receive a payment advice?",
      answer: "Yes. You'll get a summary of the early payment, discount applied, and balance payable.",
    },
    {
      question: "Are there any charges or fees?",
      answer: "No onboarding charges, no paperwork, no hidden fees.",
    },
    {
      question: "What about disputed invoices?",
      answer: "Only approved invoices are eligible. Disputed ones are excluded until resolved.",
    },
  ];

  return (
    <Box sx={{ maxWidth: 1030, mx: "auto", py: 3 }}>
      {bannerData?.hasFilledForm && (
        <Box sx={{ maxWidth: 900, mx: "auto", mb: 3 }}>
          <Box
            sx={{
              bgcolor: "#E8F5E9",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              border: "1px solid #C8E6C9",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#4CAF50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#1B5E20" }}>
              Thank you for showing interest
            </Typography>
            <Typography variant="body1" sx={{ color: "#2E7D32" }}>
              Our team will get back to you soon
            </Typography>
          </Box>
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e3a8a 0%, #3b5998 100%)",
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              Early payment program
            </Typography>
            {bannerData?.vendorName && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                for {bannerData.vendorName}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: "right" }}>
            {bannerData?.text && (
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#FFA726" }}>
                {bannerData.text} per ₹100* Invoice value
              </Typography>
            )}
            {bannerData?.rate && (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {bannerData.rate} %
                </Typography>
                <Typography variant="caption">per annum</Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(255,255,255,0.1)",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <CurrencyRupeeIcon fontSize="small" />
            <Typography variant="body2">Off-balance sheet financing</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(255,255,255,0.1)",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            <Typography variant="body2">No paperwork — opt in online</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(255,255,255,0.1)",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <PercentIcon fontSize="small" />
            <Typography variant="body2">Transparent discount rate</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(255,255,255,0.1)",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <PaymentsOutlinedIcon fontSize="small" />
            <Typography variant="body2">Improved cash flow</Typography>
          </Box>
        </Box>

        {bannerData?.creditPeriod && (
          <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", opacity: 0.7 }}>
            *Based on {bannerData.creditPeriod} days credit period. This might vary depending on your agreed credit
            period.
          </Typography>
        )}
      </Box>

      {!bannerData?.hasFilledForm && (
        <>
          {/* Get Started Form */}
          <Box sx={{ bgcolor: "white", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Get Started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete your details to activate early payments
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField
                className="update-custom-border-radius"
                label="Name*"
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={handleInputChange("name")}
              />
              <TextField
                className="update-custom-border-radius"
                label="Designation*"
                variant="outlined"
                fullWidth
                value={formData.designation}
                onChange={handleInputChange("designation")}
              />
              <TextField
                className="update-custom-border-radius"
                label="Email ID*"
                variant="outlined"
                fullWidth
                value={formData.emailId}
                onChange={handleInputChange("emailId")}
              />
              <TextField
                className="update-custom-border-radius"
                label="Contact Number*"
                variant="outlined"
                fullWidth
                value={formData.contactNumber}
                onChange={handleInputChange("contactNumber")}
              />
            </Box>

            <TextField
              className="update-custom-border-radius"
              label="Credit Period*"
              variant="outlined"
              fullWidth
              type="number"
              value={formData.creditPeriod}
              onChange={handleInputChange("creditPeriod")}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Upload GST Certificate
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
                <CustomButton variant="contained" color="inherit" component="label" size="small">
                  Choose file
                  <input type="file" hidden onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                </CustomButton>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {formData.gstCertificate ? formData.gstCertificate.name : "No file chosen"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <CustomButton
                variant="outlined"
                colorToken="violet"
                fullWidth
                startIcon={<CheckCircleOutline />}
                onClick={() => {
                  uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_EP_REQUEST_CALLBACK_CLICK.message, {
                    function: uiLoggerNamesVendorPortal.UI_SST_EP_REQUEST_CALLBACK_CLICK.functionName,
                  });
                  trackEarlyPaymentSubmitted({
                    credit_period: formData.creditPeriod,
                    has_gst_cert: !!formData.gstCertificate,
                    success: true,
                  });
                  submitFormData(RequestType.REQUEST_CALLBACK);
                }}
              >
                Request a Callback
              </CustomButton>
              <CustomButton
                variant="contained"
                colorToken="violet"
                size="large"
                fullWidth
                startIcon={<CheckCircleOutline />}
                onClick={() => {
                  uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_EP_GET_ONBOARDED_CLICK.message, {
                    function: uiLoggerNamesVendorPortal.UI_SST_EP_GET_ONBOARDED_CLICK.functionName,
                  });
                  submitFormData(RequestType.GET_ONBOARDED);
                }}
              >
                Get Onboarded
              </CustomButton>
            </Box>
          </Box>
        </>
      )}

      {/* How it Works */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}>
          How it Works?
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box sx={{ bgcolor: "#E91E63", borderRadius: 2, p: 3, color: "white" }}>
            <Box
              sx={{
                width: 37,
                height: 37,
                borderRadius: "50%",
                bgcolor: "white",
                color: "#E91E63",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                mb: 2,
              }}
            >
              1
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Brand raises invoice
            </Typography>
            <Typography variant="body2">Brand raises invoice of say ₹100 with credit period of 34 days.</Typography>
          </Box>

          <Box sx={{ bgcolor: "#00BFA5", borderRadius: 2, p: 3, color: "white" }}>
            <Box
              sx={{
                width: 37,
                height: 37,
                borderRadius: "50%",
                bgcolor: "white",
                color: "#00BFA5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                mb: 2,
              }}
            >
              2
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Invoice accepted
            </Typography>
            <Typography variant="body2">Invoice accepted by platform (no change from current process).</Typography>
          </Box>

          <Box sx={{ bgcolor: "#9C27B0", borderRadius: 2, p: 3, color: "white" }}>
            <Box
              sx={{
                width: 37,
                height: 37,
                borderRadius: "50%",
                bgcolor: "white",
                color: "#9C27B0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                mb: 2,
              }}
            >
              3
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Receive discounted value
            </Typography>
            <Typography variant="body2">
              Brand to receive discounted value in 3-4 days post GRN along with Debit Note.
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "#FF9800", borderRadius: 2, p: 3, color: "white" }}>
            <Box
              sx={{
                width: 37,
                height: 37,
                borderRadius: "50%",
                bgcolor: "white",
                color: "#FF9800",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                mb: 2,
              }}
            >
              4
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Issue credit note
            </Typography>
            <Typography variant="body2">Brand to issue credit note within 7 days of receiving payment.</Typography>
          </Box>
        </Box>
      </Box>

      {/* FAQ Section */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}>
          Frequently Asked Questions
        </Typography>

        {faqItems.map((item, index) => (
          <Accordion key={index} sx={{ mb: 1, boxShadow: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">{item.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default EarlyPaymentVendorPortal;
